import dayjs, { Dayjs } from "dayjs";

export class FilterInfo {
    text_search: string;
    teams: any[];
    vendorTeams: any[];
    order_status: ("Unordered" | "Ordered")[];

    constructor() {
        this.text_search = "";
        this.teams = [];
        this.vendorTeams = [];
        this.order_status = [];
    }

    toApiObject() {
        return {
            text_search: this.text_search,
            teams: this.teams.map((team) => (team.id ? team.id : team)),
            vendor_teams: this.vendorTeams.map((team) => (team.id ? team.id : team)),
            order_status: this.order_status,
        };
    }

    appendedToUrl(url: URL) {
        url.searchParams.set("text_search", this.text_search);

        for (const team of this.teams) {
            url.searchParams.append("teams[]", team.id ? team.id : team);
        }

        for (const vendorTeam of this.vendorTeams) {
            url.searchParams.append("vendor_teams[]", vendorTeam.id ? vendorTeam.id : vendorTeam);
        }

        for (const order_stat of this.order_status) {
            url.searchParams.append("order_status[]", order_stat);
        }

        return url;
    }

    isUnfiltered(): boolean {
        if (this.text_search !== "") {
            return false;
        }
        if (this.teams.length > 0) {
            return false;
        }
        if (this.vendorTeams.length > 0) {
            return false;
        }
        if (this.order_status.length > 0) {
            return false;
        }
        return true;
    }
}

export class ViewInfo {
    currentTab:
        "ACTION_ITEMS" | "SUMMARY" | "MATERIALS" | "ORDERS" | "ACTIVITIES" | "QUANTITIES" | "DASHBOARD" | "CONNECTOR";

    groupByValue: "NONE" | "TEAM" | "VENDOR" | "SPEC" | "PROJECT" | "SUBMITTAL" | "ACTIVITY";
    sortByValue: string;
    sortDirection: "asc" | "desc";

    materialViewValue: "SCHEDULE" | "QUANTITY";

    checklistViewValue: "chart" | "column";

    orderViewValue:
        | string
        | "ALL"
        | "SCHEDULE"
        | "ANTICIPATEDS"
        | "ACTUALS"
        | "DURATIONS"
        | "DELIVERIES"
        | "HIDE_ORDER"
        | "TIMELINE"
        | "QUANTITY"
        | "ACTIVITIES";

    constructor() {
        this.currentTab = "DASHBOARD";
        this.groupByValue = "SPEC";
        this.sortByValue = "ID";
        this.materialViewValue = "SCHEDULE";
        this.orderViewValue = "SCHEDULE";
        this.sortDirection = "asc";
        this.checklistViewValue = "chart";
    }
}

export class OrderExtra {
    order_id: number;
    order_name: string;
    material_name: string;

    constructor(order_id: number, order_name: string, material_name: string) {
        this.order_id = order_id;
        this.order_name = order_name;
        this.material_name = material_name;
    }

    static fromApiObject(obj: any): OrderExtra {
        return new OrderExtra(obj.order_id, obj.order_name, obj.material_name);
    }
}

export class MaterialExtra {
    material_id: number;
    material_name: string;

    constructor(material_id: number, material_name: string) {
        this.material_id = material_id;
        this.material_name = material_name;
    }

    static fromApiObject(obj: any): MaterialExtra {
        return new MaterialExtra(obj.material_id, obj.material_name);
    }
}

export class ChangeHistoryPoint {
    id: number;
    change: any;
    created_at: Dayjs;

    user: null | { id: number; fullname: string; nickname: string };

    auditable_type: "Material" | "Order";
    extra: OrderExtra | MaterialExtra;

    constructor(
        id: number,
        change: any,
        created_at: Dayjs,
        user: { id: number; fullname: string; nickname: string } | null,
        auditable_type: "Material" | "Order",
        extra: OrderExtra | MaterialExtra
    ) {
        this.id = id;
        this.change = change;
        this.created_at = created_at;
        this.user = user;
        this.auditable_type = auditable_type;
        this.extra = extra;
    }

    changeValues = (column_name: string): { previous: string; now: string } | null => {
        const found_row = this.change[column_name];

        if (!found_row) {
            return null;
        }

        switch (true) {
            // Catch all of the filtered out cases first
            case /.*_updated/gm.test(column_name): // These are "hidden" values so users do not need to see these
                return null;
            case /.*_locked/gm.test(column_name): // These are "hidden" values so users do not need to see these
                return null;
        }

        return { previous: found_row[0], now: found_row[1] };
    };

    changeValueStrings = (column_name: string): { previous: string; now: string } | null => {
        const changeValues = this.changeValues(column_name);

        if (!changeValues) {
            return null;
        }

        // If it's one of our special date fields, format it properly
        if (
            column_name == "order_by" ||
            column_name == "ordered" ||
            column_name == "required_on_jobsite" ||
            column_name == "anticipated_on_jobsite" ||
            column_name == "anticipated_order" ||
            column_name == "submit_by" ||
            column_name == "approve_by" ||
            column_name == "approval_date" ||
            column_name == "submit_date" ||
            column_name == "next_order_by" ||
            column_name == "next_required_on_jobsite" ||
            column_name == "anticipated_approval" ||
            column_name == "anticipated_submit"
        ) {
            changeValues.previous = this.formattedDateFromString(changeValues.previous);
            changeValues.now = this.formattedDateFromString(changeValues.now);
        }

        return changeValues;
    };

    columnNameToFormatted = (column: string): string => {
        const parts = column.split("_");
        const capitalized = parts.map((item) => item[0].toUpperCase() + item.substring(1));

        return capitalized.join(" ");
    };

    renderableChanges = (): string[] => {
        if (!this.change) {
            return [];
        }

        const changes: string[] = [];
        Object.entries(this.change).forEach(([change_name]) => {
            const change = this.changeValueStrings(change_name);

            switch (true) {
                case change_name.includes("material_id"):
                    changes.push(`Created Order for Material (${this.extra.material_name || "Material"})`);
                    return;
                case change_name.includes("delivery_id"):
                    changes.push(`Created a Delivery for Order`);
                    return;
                case change_name.includes("activity_ids"):
                    changes.push(`Updated Connected Activities`);
                    return;
                case !change:
                    // Skip if no changes to track as we already printed the stuff that does not track before's and after's
                    return;
                case change_name.includes("order_by"):
                    changes.push(`Updated 'Order Deadline' from ${change.previous} to ${change.now}`);
                    return;
                case change_name.includes("ordered"):
                    changes.push(`Updated 'Order Date' from ${change.previous} to ${change.now}`);
                    return;
                case change_name.includes("required_on_jobsite"):
                    changes.push(`Updated 'Required On Site' from ${change.previous} to ${change.now}`);
                    return;
                case change_name.includes("order_name"):
                    changes.push(
                        `Updated 'Order Name' from ${
                            change.previous && change.previous.length > 0 ? change.previous : "empty"
                        } to ${change.now && change.now.length > 0 ? change.now : "empty"}`
                    );
                    return;
                case change_name.includes("status"):
                    changes.push(
                        `Updated 'Status' from '${OrderStatus.fromNumber(
                            parseInt(change.previous)
                        ).pretty()}' to '${OrderStatus.fromNumber(parseInt(change.now)).pretty()}'`
                    );
                    return;
                case change_name.includes("NEW"):
                    changes.push("Created");
                    return;
                default:
                    changes.push(
                        `Updated '${this.columnNameToFormatted(change_name)}' from ${
                            change.previous ? change.previous : "empty"
                        } to ${change.now ? change.now : "empty"}`
                    );
                    return;
            }
        });

        return changes;
    };

    static fromApiObject(obj: any): ChangeHistoryPoint {
        const extra =
            obj.auditable_type == "Material"
                ? MaterialExtra.fromApiObject(obj.extra)
                : OrderExtra.fromApiObject(obj.extra);

        return new ChangeHistoryPoint(
            obj.id,
            typeof obj.change == "string" ? JSON.parse(obj.change) : obj.change,
            dayjs(obj.created_at),
            obj.user,
            obj.auditable_type,
            extra
        );
    }

    private formattedDateFromString = (date_str: string, default_today: boolean = false): string => {
        if (!default_today && (!date_str || date_str.length == 0)) {
            return "empty";
        }

        const date = new Date(date_str);

        return date.toLocaleDateString("en-US", {
            month: "numeric",
            day: "numeric",
            year: "2-digit",
        });
    };
}

export class ChangeHistoryMessage {
    id: number;
    created_at: Dayjs;
    updated_at: Dayjs;
    user: any;
    message: string;
    files: any[];

    constructor(id: number, created_at: Dayjs, updated_at: Dayjs, user: any, message: string, files: any[]) {
        this.id = id;
        this.created_at = created_at;
        this.updated_at = updated_at;
        this.user = user;
        this.message = message;
        this.files = files;
    }

    static fromApiObject(message: any): ChangeHistoryMessage {
        return new ChangeHistoryMessage(
            message.id,
            dayjs(message.created_at),
            dayjs(message.updated_at),
            message.user,
            message.body,
            message.files
        );
    }
}

export class CheckInResponse {
    response: number = 0;
    responseName: string = "";

    static ON_TRACK: CheckInResponse = new CheckInResponse(0, "ON_TRACK");
    static OFF_TRACK: CheckInResponse = new CheckInResponse(1, "OFF_TRACK");

    constructor(response: number, responseName: string) {
        this.response = response;
        this.responseName = responseName;
    }

    static fromID(id: number): CheckInResponse {
        if (id == this.ON_TRACK.response) {
            return this.ON_TRACK;
        } else if (id == this.OFF_TRACK.response) {
            return this.OFF_TRACK;
        }

        return this.ON_TRACK;
    }

    getName(): string {
        return this.responseName;
    }
}

export class OrderStatus {
    type: number = 0;
    typeName: string = "";

    static NEEDS_ROJ: OrderStatus = new OrderStatus(5, "needs_roj");
    static NEEDS_LEAD_TIME: OrderStatus = new OrderStatus(10, "needs_lead_time");
    static NEEDS_MATERIAL_SUBMITTED: OrderStatus = new OrderStatus(15, "needs_material_submitted");
    static NEEDS_MATERIAL_APPROVED: OrderStatus = new OrderStatus(20, "needs_material_approved");
    static NEEDS_ORDERED: OrderStatus = new OrderStatus(25, "needs_ordered");
    static NEEDS_SCHEDULED: OrderStatus = new OrderStatus(30, "needs_scheduled");
    static NEEDS_RECEIVED: OrderStatus = new OrderStatus(35, "needs_received");
    static COMPLETE: OrderStatus = new OrderStatus(40, "complete");

    constructor(type: number, typeName: string) {
        this.type = type;
        this.typeName = typeName;
    }

    static fromNumber(status: number): OrderStatus {
        if (status == this.NEEDS_ROJ.type) {
            return this.NEEDS_ROJ;
        } else if (status == this.NEEDS_LEAD_TIME.type) {
            return this.NEEDS_LEAD_TIME;
        } else if (status == this.NEEDS_MATERIAL_SUBMITTED.type) {
            return this.NEEDS_MATERIAL_SUBMITTED;
        } else if (status == this.NEEDS_MATERIAL_APPROVED.type) {
            return this.NEEDS_MATERIAL_APPROVED;
        } else if (status == this.NEEDS_ORDERED.type) {
            return this.NEEDS_ORDERED;
        } else if (status == this.NEEDS_SCHEDULED.type) {
            return this.NEEDS_SCHEDULED;
        } else if (status == this.NEEDS_RECEIVED.type) {
            return this.NEEDS_RECEIVED;
        } else if (status == this.COMPLETE.type) {
            return this.COMPLETE;
        }

        return this.NEEDS_ROJ;
    }

    static fromString(status: string | undefined): OrderStatus {
        if (!status) {
            return this.NEEDS_ROJ;
        }

        if (status == this.NEEDS_ROJ.typeName) {
            return this.NEEDS_ROJ;
        } else if (status == this.NEEDS_LEAD_TIME.typeName) {
            return this.NEEDS_LEAD_TIME;
        } else if (status == this.NEEDS_MATERIAL_SUBMITTED.typeName) {
            return this.NEEDS_MATERIAL_SUBMITTED;
        } else if (status == this.NEEDS_MATERIAL_APPROVED.typeName) {
            return this.NEEDS_MATERIAL_APPROVED;
        } else if (status == this.NEEDS_ORDERED.typeName) {
            return this.NEEDS_ORDERED;
        } else if (status == this.NEEDS_SCHEDULED.typeName) {
            return this.NEEDS_SCHEDULED;
        } else if (status == this.NEEDS_RECEIVED.typeName) {
            return this.NEEDS_RECEIVED;
        } else if (status == this.COMPLETE.typeName) {
            return this.COMPLETE;
        }

        return this.NEEDS_ROJ;
    }

    isAtLeast(status: OrderStatus): boolean {
        return this.type >= status.type;
    }

    isAtMost(status: OrderStatus): boolean {
        return this.type <= status.type;
    }

    pretty(): string {
        return this.typeName
            .split("_")
            .map((str) => (str.length > 0 ? str[0] + str.slice(1).toLowerCase() : null))
            .filter((str) => str != null)
            .join(" ");
    }
}
